import React, { useState, useEffect, useRef } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Card,
  Avatar,
  Typography,
  Box,
  InputAdornment,
  Skeleton,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
  Alert,
  CircularProgress,
  Menu,
  MenuItem,
  ListItemIcon
} from '@mui/material';
import {
  Search,
  Assignment,
  Visibility,
  Close,
  Email,
  Phone,
  LocationOn,
  AttachFile,
  GetApp,
  VideoCall,
  Person,
  Business,
  CalendarToday,
  CheckCircle,
  Task,
  Subject,
  Description,
  Support as SupportIcon,
  ArrowDropDown,
  PersonOutline,
  Chat as ChatIcon,
  Send
} from '@mui/icons-material';
import axios from 'axios';
import { useSocket } from '../../components/Hooks/SocketProvider';
import { BASE_URL } from '../../services/configUrls';


const StaffMeetingsTable = () => {
  const [meetings, setMeetings] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  
  // Solve dialog states
  const [openSolveDialog, setOpenSolveDialog] = useState(false);
  const [closingRemark, setClosingRemark] = useState('');
  const [solvingLoading, setSolvingLoading] = useState(false);
  const [solveError, setSolveError] = useState(null);
  const [solveSuccess, setSolveSuccess] = useState(null);

  // Support dropdown states
  const [anchorEl, setAnchorEl] = useState(null);
  const [supportTeam, setSupportTeam] = useState(null);
  const [loadingSupportTeam, setLoadingSupportTeam] = useState(false);
  const [supportError, setSupportError] = useState(null);
  const [currentMeetingForSupport, setCurrentMeetingForSupport] = useState(null);

  // Chat dialog states
  const [openChatDialog, setOpenChatDialog] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [loadingChat, setLoadingChat] = useState(false);
  const [chatError, setChatError] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);

  // WebSocket connection
  const { socket, lastMessage, userId, connectionStatus } = useSocket();

  // Refs for WebSocket callbacks
  const chatMessagesRef = useRef(chatMessages);
  const chatModalRequestRef = useRef(null);
  const isChatModalOpenRef = useRef(openChatDialog);
  const chatMessagesEndRef = useRef(null);

  // Update refs whenever state changes
  useEffect(() => {
    chatMessagesRef.current = chatMessages;
    scrollToBottom();
  }, [chatMessages]);

  useEffect(() => {
    chatModalRequestRef.current = selectedMeeting;
  }, [selectedMeeting]);

  useEffect(() => {
    isChatModalOpenRef.current = openChatDialog;
  }, [openChatDialog]);


  // Access token management
  const getAccessToken = () => {
    return localStorage.getItem('accessToken') || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiJlNDBmNWQ5NS04MzAwLTQ1MWEtYTZlZi0wYWZiN2FkNGFmYmMiLCJleHAiOjE3NjI0OTU5NDB9.y0SHwlfk8mDlRVVpuQwfpO89_RFl3I1JA7JAQt6JGjE';
  };


  // Scroll to bottom function
  const scrollToBottom = () => {
    chatMessagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };


  // WebSocket message listener
  useEffect(() => {
    if (!socket) return;

    const handleWebSocketMessage = (event) => {
      console.log('📩 Received WebSocket message:', event);
      try {
        // Robust parser that handles both single and double stringification
        const parseMessage = (data) => {
          let parsed = data;
          let attempts = 0;
          const maxAttempts = 3;
          
          while (typeof parsed === 'string' && attempts < maxAttempts) {
            try {
              const nextParse = JSON.parse(parsed);
              parsed = nextParse;
              attempts++;
              console.log(`🔍 Parse attempt ${attempts}:`, parsed, 'Type:', typeof parsed);
            } catch (e) {
              console.log('⚠️ Parse failed, using current result');
              break;
            }
          }
          
          return parsed;
        };

        const messageData = parseMessage(event.data);
        console.log('✅ Final parsed message data:', messageData);
        
        const isChatOpen = isChatModalOpenRef.current;
        const currentRequest = chatModalRequestRef.current;
        const chatResponse = messageData.chat_response;
        
        if (isChatOpen && currentRequest && chatResponse && 
            chatResponse.staff_meting_id === currentRequest.meeting_id) {
          
          console.log('✅ Processing message for staff meeting');
          
          setChatMessages(prevMessages => {
            const filteredMessages = prevMessages.filter(msg => !msg.sending);
            console.log('🗂️ Filtered Messages:', filteredMessages);
            
            const messageExists = filteredMessages.some(msg => 
              msg.id === chatResponse.id ||
              (msg.message === chatResponse.message && 
               Math.abs(new Date(msg.created_at) - new Date(chatResponse.created_at)) < 2000)
            );
            
            if (messageExists) {
              return filteredMessages;
            }
            
            console.log('➕ Adding message:', chatResponse);
            return [...filteredMessages, chatResponse];
          });
        } else {
          console.log('❌ Conditions not met for processing');
        }
      } catch (error) {
        console.error('💥 Error in WebSocket handler:', error);
      }
    };

    socket.addEventListener('message', handleWebSocketMessage);

    return () => {
      socket.removeEventListener('message', handleWebSocketMessage);
    };
  }, [socket]);


  // Fetch chat messages for a meeting
  const fetchChatMessages = async (meetingId) => {
    setLoadingChat(true);
    setChatError(null);
    
    try {
      const config = {
        method: 'get',
        maxBodyLength: Infinity,
        url: `${BASE_URL}/staffmetting/staffMeetingChat/${meetingId}`,
        headers: { 
          'Authorization': `Bearer ${getAccessToken()}`
        }
      };

      const response = await axios.request(config);
      console.log('Chat messages response:', response.data);
      
      if (response.data && response.data.messages) {
        setChatMessages(response.data.messages);
      } else {
        setChatMessages([]);
      }
    } catch (error) {
      console.error('Error fetching chat messages:', error);
      if (error.response) {
        setChatError(`Error ${error.response.status}: ${error.response.data?.message || 'Unable to load chat messages'}`);
      } else if (error.request) {
        setChatError('Network error. Please check your connection.');
      } else {
        setChatError('Unable to load chat messages. Please try again.');
      }
    } finally {
      setLoadingChat(false);
    }
  };


  // Send message via WebSocket
  const sendChatMessage = async () => {
    if (!newMessage.trim() || !selectedMeeting) {
      return;
    }

    if (!socket || socket.readyState !== WebSocket.OPEN) {
      setChatError('Chat connection not available. Please refresh the page.');
      return;
    }

    setSendingMessage(true);
    
    try {
      const messageData = {
        staff_meting_id: selectedMeeting.meeting_id,
        message: newMessage.trim(),
        made_by_user_id: userId,
        role: "staff",
        is_spoc: "0"
      };

      console.log('Sending message via WebSocket:', messageData);
      
      // Add optimistic update
      const tempMessage = {
        id: `temp-${Date.now()}`,
        message: newMessage.trim(),
        role: "staff",
        is_spoc: "0",
        created_at: new Date().toISOString(),
        sending: true
      };
      
      setChatMessages(prev => [...prev, tempMessage]);
      
      // Send via WebSocket
      socket.send(JSON.stringify(messageData));
      
      // Clear input field
      setNewMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
      setChatError('Failed to send message. Please try again.');
      
      // Remove failed message from UI
      setChatMessages(prev => prev.filter(msg => !msg.sending));
    } finally {
      setSendingMessage(false);
    }
  };


  // Handle chat dialog open
  const handleOpenChatDialog = (meeting) => {
    setSelectedMeeting(meeting);
    setOpenChatDialog(true);
    setChatMessages([]);
    setNewMessage('');
    setChatError(null);
    
    // Fetch chat messages
    fetchChatMessages(meeting.meeting_id);
  };


  // Handle chat dialog close
  const handleCloseChatDialog = () => {
    setOpenChatDialog(false);
    setSelectedMeeting(null);
    setChatMessages([]);
    setNewMessage('');
    setChatError(null);
  };


  // Format timestamp for chat messages
  const formatChatTimestamp = (dateString) => {
    try {
      return new Date(dateString).toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return dateString;
    }
  };


  // Get message sender info
  const getMessageSenderInfo = (message) => {
    const isSpoc = message.is_spoc === "1" || message.is_spoc === 1;
    return {
      isSpoc,
      label: isSpoc ? 'SPOC' : 'Staff',
      color: isSpoc ? '#3b82f6' : '#10b981',
      bgColor: isSpoc ? '#dbeafe' : '#d1fae5',
      avatarBg: isSpoc ? 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)' : 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
    };
  };


  // Fetch support team dropdown
  const fetchSupportTeam = async (meeting) => {
    setLoadingSupportTeam(true);
    setSupportError(null);
    
    try {
      const formData = new FormData();
      formData.append('subjects', meeting.topic || 'Staff meeting support request');
      formData.append('description', meeting.description || 'Support needed for staff meeting');

      const config = {
        method: 'get',
        maxBodyLength: Infinity,
        url: `${BASE_URL}/staffmetting/validate_dropdown_emails`,
        headers: { 
          'Authorization': `Bearer ${getAccessToken()}`
        },
        data: formData
      };

      const response = await axios.request(config);
      console.log('Support Team Response:', response.data);
      
      if (response.data) {
        setSupportTeam(response.data);
      } else {
        setSupportError('No support team data found');
      }
    } catch (error) {
      console.error('Error fetching support team:', error);
      if (error.response) {
        setSupportError(`Error ${error.response.status}: ${error.response.data?.message || 'Unable to load support team'}`);
      } else if (error.request) {
        setSupportError('Network error. Please check your connection.');
      } else {
        setSupportError('Unable to load support team. Please try again.');
      }
    } finally {
      setLoadingSupportTeam(false);
    }
  };


  // Handle support dropdown open
  const handleSupportDropdownClick = (event, meeting) => {
    setAnchorEl(event.currentTarget);
    setCurrentMeetingForSupport(meeting);
    setSupportTeam(null);
    fetchSupportTeam(meeting);
  };


  // Handle support dropdown close
  const handleSupportDropdownClose = () => {
    setAnchorEl(null);
  };


  // Handle support member selection - WITH API INTEGRATION
  const handleSupportMemberSelect = async (memberKey, memberData) => {
    console.log('Selected support member:', memberData);
    console.log('For meeting:', currentMeetingForSupport);
    
    try {
      // Prepare the data for the API call
      const data = JSON.stringify({
        staff_meetings_id: currentMeetingForSupport.meeting_id,
        assigned_userid: memberData.user_id
      });

      const config = {
        method: 'post',
        maxBodyLength: Infinity,
        url: `${BASE_URL}/staffmetting/updateStaffQuestion`,
        headers: { 
          'Content-Type': 'application/json', 
          'Authorization': `Bearer ${getAccessToken()}`
        },
        data: data
      };

      // Show loading state
      setSupportError(null);
      setLoadingSupportTeam(true);

      const response = await axios.request(config);
      
      console.log('Assignment response:', response.data);

      // Update the meeting in the local state with the assigned user
      setMeetings(prevMeetings => 
        prevMeetings.map(meeting => 
          meeting.meeting_id === currentMeetingForSupport.meeting_id 
            ? { ...meeting, assigned_userid: memberData.user_id, assigned_user_name: memberData.role_name }
            : meeting
        )
      );

      // Show success message
      alert(`Successfully assigned to: ${memberData.role_name}\nEmail: ${memberData.email}\nUser ID: ${memberData.user_id}\n\nMeeting: ${currentMeetingForSupport.topic}`);
      
      // Close the dropdown
      handleSupportDropdownClose();
      
    } catch (error) {
      console.error('Error assigning support member:', error);
      
      // Handle error
      let errorMessage = 'Failed to assign support member. ';
      if (error.response) {
        errorMessage += `Error ${error.response.status}: ${error.response.data?.message || 'Please try again.'}`;
      } else if (error.request) {
        errorMessage += 'Network error. Please check your connection.';
      } else {
        errorMessage += 'An error occurred. Please try again.';
      }
      
      setSupportError(errorMessage);
      alert(errorMessage);
    } finally {
      setLoadingSupportTeam(false);
    }
  };


  // Fetch meetings from API
  const fetchMeetings = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const config = {
        method: 'get',
        maxBodyLength: Infinity,
        url: `${BASE_URL}/staffmetting/staff_mettings_2`,
        headers: { 
          'Authorization': `Bearer ${getAccessToken()}`,
          'Content-Type': 'application/json'
        }
      };

      const response = await axios.request(config);
      console.log('API Response:', response.data);
      
      if (response.data && response.data.data) {
        setMeetings(response.data.data);
      } else {
        setError('No meetings data found');
      }
    } catch (error) {
      console.error('Error fetching meetings:', error);
      if (error.response) {
        const status = error.response.status;
        if (status === 401) {
          setError('Authentication failed. Please check your access token.');
        } else if (status === 403) {
          setError('Access denied. You do not have permission to view this data.');
        } else if (status === 404) {
          setError('API endpoint not found. Please check the server configuration.');
        } else if (status >= 500) {
          setError('Server error. Please try again later.');
        } else {
          setError(`Error ${status}: ${error.response.data?.message || 'Unknown error occurred'}`);
        }
      } else if (error.request) {
        setError('Network error. Please check your internet connection and server status.');
      } else {
        setError('Unable to load meetings. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };


  // Initial data fetch
  useEffect(() => {
    fetchMeetings();
  }, []);


  // Filter meetings based on search term
  const filteredMeetings = meetings.filter(meeting => 
    meeting.educator_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    meeting.topic?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    meeting.institute_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    meeting.educator_email?.toLowerCase().includes(searchTerm.toLowerCase())
  );


  // Dialog handlers
  const handleOpenViewDialog = (meeting) => {
    setSelectedMeeting(meeting);
    setOpenViewDialog(true);
  };


  const handleCloseViewDialog = () => {
    setOpenViewDialog(false);
    setSelectedMeeting(null);
  };


  // Solve dialog handlers
  const handleOpenSolveDialog = (meeting) => {
    setSelectedMeeting(meeting);
    setClosingRemark('');
    setSolveError(null);
    setSolveSuccess(null);
    setOpenSolveDialog(true);
  };


  const handleCloseSolveDialog = () => {
    setOpenSolveDialog(false);
    setSelectedMeeting(null);
    setClosingRemark('');
    setSolveError(null);
    setSolveSuccess(null);
  };


  // Handle solve meeting
  const handleSolveMeeting = async () => {
    if (!closingRemark.trim()) {
      setSolveError('Please provide a closing remark explaining how the issue has been resolved.');
      return;
    }

    setSolvingLoading(true);
    setSolveError(null);

    try {
      const data = {
        staff_meetings_id: selectedMeeting.meeting_id,
        status: "solved",
        closing_remark: closingRemark.trim()
      };

      console.log('Sending solve request with data:', data);

      const config = {
        method: 'post',
        maxBodyLength: Infinity,
        url: `${BASE_URL}/staffmetting/updateStaffQuestion`,
        headers: { 
          'Content-Type': 'application/json', 
          'Authorization': `Bearer ${getAccessToken()}`
        },
        data: JSON.stringify(data)
      };

      const response = await axios.request(config);
      
      if (response.data.status || response.status === 200) {
        setSolveSuccess('Meeting has been successfully marked as solved!');
        
        setMeetings(prevMeetings => 
          prevMeetings.map(meeting => 
            meeting.meeting_id === selectedMeeting.meeting_id 
              ? { ...meeting, status: 'solved', closing_remark: closingRemark.trim() }
              : meeting
          )
        );

        setTimeout(() => {
          handleCloseSolveDialog();
        }, 2000);
      } else {
        setSolveError('Failed to update meeting status. Please try again.');
      }
    } catch (error) {
      console.error('Error solving meeting:', error);
      if (error.response) {
        const status = error.response.status;
        if (status === 401) {
          setSolveError('Authentication failed. Please login again.');
        } else if (status === 403) {
          setSolveError('You do not have permission to update this meeting.');
        } else {
          setSolveError(`Error ${status}: ${error.response.data?.message || 'Failed to update meeting status'}`);
        }
      } else if (error.request) {
        setSolveError('Network error. Please check your connection and try again.');
      } else {
        setSolveError('Error occurred while updating meeting status. Please try again.');
      }
    } finally {
      setSolvingLoading(false);
    }
  };


  // Handle meeting link click
  const handleJoinMeeting = (meetingLink) => {
    if (meetingLink) {
      const url = meetingLink.startsWith('http') ? meetingLink : `https://${meetingLink}`;
      window.open(url, '_blank');
    }
  };


  // Download attachment handler
  const handleDownloadAttachment = (attachment) => {
    if (attachment.attachment_name) {
      window.open(attachment.attachment_name, '_blank');
    }
  };


  // Get file type from URL
  const getFileIcon = (url) => {
    const extension = url?.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'pdf': return '📄';
      case 'doc':
      case 'docx': return '📝';
      case 'xls':
      case 'xlsx': return '📊';
      case 'png':
      case 'jpg':
      case 'jpeg': return '🖼️';
      case 'pages': return '📃';
      default: return '📎';
    }
  };


  // Get file name from URL
  const getFileName = (url) => {
    if (!url) return 'Unknown File';
    const fileName = url.split('/').pop();
    const cleanFileName = fileName.replace(/^[a-f0-9\-]{36}/i, '');
    return cleanFileName || fileName;
  };


  // Get file size (placeholder function)
  const getFileSize = (url) => {
    return 'Unknown size';
  };


  // Format date
  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };


  // Get initials for avatar
  const getInitials = (name) => {
    return name?.split(' ').map(word => word[0]).join('').toUpperCase() || 'NA';
  };


  // Get status chip color and text
  const getStatusChip = (meeting) => {
    const status = meeting.status || 'pending';
    const colors = {
      pending: { color: '#f59e0b', bg: '#fef3c7', text: 'Pending' },
      solved: { color: '#10b981', bg: '#d1fae5', text: 'Solved' },
      in_progress: { color: '#3b82f6', bg: '#dbeafe', text: 'In Progress' }
    };
    
    return colors[status] || colors.pending;
  };

  // Get role badge color
  const getRoleBadgeColor = (roleKey) => {
    const colors = {
      support: { bg: '#dbeafe', color: '#1e40af', label: 'Support' },
      lead_ops: { bg: '#fef3c7', color: '#92400e', label: 'Lead Operations' },
      bd: { bg: '#d1fae5', color: '#065f46', label: 'Business Development' }
    };
    return colors[roleKey] || { bg: '#f3f4f6', color: '#374151', label: roleKey };
  };

  return (
    <div className="min-h-screen p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 flex items-center justify-center shadow-lg">
            <VideoCall className="text-white text-lg" />
          </div>
          <div>
            <Typography variant="h5" className="font-bold text-gray-900 tracking-tight">
              Staff Meetings Management
            </Typography>
            <Typography variant="body2" className="text-gray-500 font-medium text-xs">
              View and manage all staff meetings and discussions
            </Typography>
          </div>
        </div>

        {/* WebSocket Connection Status */}
        <Card className="border-0 shadow-lg shadow-black/5 mb-4">
          <Box className="p-3 flex items-center gap-2">
            <div 
              className={`w-2 h-2 rounded-full ${
                connectionStatus === 'connected' ? 'bg-green-500' : 
                connectionStatus === 'connecting' ? 'bg-yellow-500' : 
                'bg-red-500'
              }`}
            />
            <Typography variant="body2" style={{ fontSize: '11px' }}>
              WebSocket: {connectionStatus} {userId && `(User ID: ${userId})`}
            </Typography>
          </Box>
        </Card>

        {/* Search */}
        <Card className="border-0 shadow-lg shadow-black/5 mb-6">
          <Box className="p-4">
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search by educator name, topic, institute..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search className="text-gray-400" />
                  </InputAdornment>
                ),
                className: "rounded-lg bg-gray-50",
                style: { fontSize: '12px' }
              }}
            />
          </Box>
        </Card>
      </div>

      {/* Error Message */}
      {error && (
        <Card className="border-0 shadow-lg mb-6 bg-red-50 border-red-200">
          <Box className="p-4">
            <Alert severity="error" className="mb-2">
              <Typography variant="body2" className="text-red-700">
                {error}
              </Typography>
            </Alert>
            <Button
              size="small"
              onClick={fetchMeetings}
              className="mt-2"
              variant="outlined"
              color="error"
            >
              Retry
            </Button>
          </Box>
        </Card>
      )}

      {/* Meetings Table */}
      <Card className="border-0 shadow-xl shadow-black/5 overflow-hidden">
        <TableContainer className="max-h-[70vh]">
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ 
                  backgroundColor: '#2563eb', color: 'white', fontWeight: 'bold',
                  fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em', border: 0
                }}>
                  Meeting ID
                </TableCell>
                <TableCell sx={{ 
                  backgroundColor: '#2563eb', color: 'white', fontWeight: 'bold',
                  fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em', border: 0
                }}>
                  Educator
                </TableCell>
                <TableCell sx={{ 
                  backgroundColor: '#2563eb', color: 'white', fontWeight: 'bold',
                  fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em', border: 0
                }}>
                  Topic
                </TableCell>
                <TableCell sx={{ 
                  backgroundColor: '#2563eb', color: 'white', fontWeight: 'bold',
                  fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em', border: 0
                }}>
                  Institute
                </TableCell>
                <TableCell sx={{ 
                  backgroundColor: '#2563eb', color: 'white', fontWeight: 'bold',
                  fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em', border: 0
                }}>
                  Status
                </TableCell>
                <TableCell sx={{ 
                  backgroundColor: '#2563eb', color: 'white', fontWeight: 'bold',
                  fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em', border: 0
                }}>
                  Created Date
                </TableCell>
                <TableCell sx={{ 
                  backgroundColor: '#2563eb', color: 'white', fontWeight: 'bold',
                  fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em', border: 0,
                  textAlign: 'center'
                }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from({ length: 3 }).map((_, index) => (
                  <TableRow key={index}>
                    {Array.from({ length: 7 }).map((_, cellIndex) => (
                      <TableCell key={cellIndex}>
                        <Skeleton variant="text" width="100%" height={20} />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : filteredMeetings.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12">
                    <div className="flex flex-col items-center gap-3">
                      <Search className="text-gray-400 text-3xl" />
                      <Typography variant="body1" className="text-gray-500 text-sm">
                        {meetings.length === 0 ? 'No meetings found' : 'No meetings match your search'}
                      </Typography>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                filteredMeetings.map((meeting, index) => {
                  const statusInfo = getStatusChip(meeting);
                  return (
                    <TableRow 
                      key={meeting.meeting_id} 
                      className={`hover:bg-blue-50/50 transition-colors duration-200 ${
                        index % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'
                      }`}
                    >
                      <TableCell className="border-0 py-3">
                        <Typography variant="body2" className="font-bold text-blue-600" style={{ fontSize: '11px' }}>
                          #{meeting.meeting_id}
                        </Typography>
                      </TableCell>
                      
                      <TableCell className="border-0 py-3">
                        <div className="flex items-center gap-2">
                          <Avatar 
                            className="w-7 h-7 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-bold"
                            style={{ fontSize: '10px' }}
                          >
                            {getInitials(meeting.educator_name)}
                          </Avatar>
                          <div>
                            <Typography variant="body2" className="font-semibold text-gray-900" style={{ fontSize: '11px' }}>
                              {meeting.educator_name}
                            </Typography>
                            <Typography variant="body2" className="text-gray-500" style={{ fontSize: '10px' }}>
                              {meeting.educator_email}
                            </Typography>
                          </div>
                        </div>
                      </TableCell>
                      
                      <TableCell className="border-0 py-3 max-w-xs">
                        <Tooltip title={meeting.topic} arrow placement="top">
                          <Typography variant="body2" className="text-gray-700 truncate" style={{ fontSize: '11px' }}>
                            {meeting.topic?.length > 40 
                              ? `${meeting.topic.substring(0, 40)}...` 
                              : meeting.topic}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      
                      <TableCell className="border-0 py-3">
                        <div>
                          <Typography variant="body2" className="text-gray-700 font-medium" style={{ fontSize: '11px' }}>
                            {meeting.institute_name}
                          </Typography>
                          <Typography variant="body2" className="text-gray-500" style={{ fontSize: '10px' }}>
                            {meeting.state_name}
                          </Typography>
                        </div>
                      </TableCell>

                      <TableCell className="border-0 py-3">
                        <Chip
                          label={statusInfo.text}
                          size="small"
                          style={{
                            backgroundColor: statusInfo.bg,
                            color: statusInfo.color,
                            fontWeight: 'bold',
                            fontSize: '10px',
                            height: '22px',
                            borderRadius: '6px'
                          }}
                        />
                      </TableCell>
                      
                      <TableCell className="border-0 py-3">
                        <Typography variant="body2" className="text-gray-600" style={{ fontSize: '11px' }}>
                          {formatDate(meeting.created_at)}
                        </Typography>
                      </TableCell>
                      
                      <TableCell className="border-0 py-3 text-center">
                        <div className="flex justify-center gap-1">
                          <Tooltip title="View Details" arrow>
                            <IconButton 
                              size="small"
                              onClick={() => handleOpenViewDialog(meeting)}
                              className="text-blue-600 hover:bg-blue-50"
                            >
                              <Visibility style={{ fontSize: '16px' }} />
                            </IconButton>
                          </Tooltip>

                          {/* Chat Button */}
                          <Tooltip title="Open Chat" arrow>
                            <IconButton 
                              size="small"
                              onClick={() => handleOpenChatDialog(meeting)}
                              className="text-purple-600 hover:bg-purple-50"
                            >
                              <ChatIcon style={{ fontSize: '16px' }} />
                            </IconButton>
                          </Tooltip>
                          
                          <Tooltip title="Assign to Support Team" arrow>
                            <IconButton 
                              size="small"
                              onClick={(e) => handleSupportDropdownClick(e, meeting)}
                              className="text-orange-600 hover:bg-orange-50"
                            >
                              <SupportIcon style={{ fontSize: '16px' }} />
                            </IconButton>
                          </Tooltip>
                          
                          {meeting.status === 'pending' && (
                            <Tooltip title="Mark as Solved" arrow>
                              <IconButton 
                                size="small"
                                onClick={() => handleOpenSolveDialog(meeting)}
                                className="text-green-600 hover:bg-green-50"
                              >
                                <CheckCircle style={{ fontSize: '16px' }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          
                          {meeting.metting_link && (
                            <Tooltip title="Join Meeting" arrow>
                              <IconButton 
                                size="small"
                                onClick={() => handleJoinMeeting(meeting.metting_link)}
                                className="text-indigo-600 hover:bg-indigo-50"
                              >
                                <VideoCall style={{ fontSize: '16px' }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Support Team Dropdown Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleSupportDropdownClose}
        PaperProps={{
          style: {
            borderRadius: '12px',
            boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
            minWidth: '320px',
            marginTop: '8px'
          }
        }}
      >
        <Box className="px-4 py-3 border-b border-gray-200">
          <Typography variant="h6" className="font-bold text-gray-900" style={{ fontSize: '14px' }}>
            Assign to Support Team
          </Typography>
          <Typography variant="body2" className="text-gray-500" style={{ fontSize: '11px' }}>
            Select a team member to handle this meeting
          </Typography>
        </Box>

        {loadingSupportTeam ? (
          <Box className="p-4 flex justify-center">
            <CircularProgress size={24} />
          </Box>
        ) : supportError ? (
          <Box className="p-4">
            <Alert severity="error" className="text-xs">
              {supportError}
            </Alert>
            <Button 
              size="small" 
              onClick={() => fetchSupportTeam(currentMeetingForSupport)}
              className="mt-2"
              fullWidth
            >
              Retry
            </Button>
          </Box>
        ) : supportTeam ? (
          <>
            {Object.entries(supportTeam).map(([key, member]) => {
              const roleInfo = getRoleBadgeColor(key);
              return (
                <MenuItem 
                  key={key}
                  onClick={() => handleSupportMemberSelect(key, member)}
                  className="py-3 px-4 hover:bg-blue-50"
                >
                  <ListItemIcon>
                    <Avatar 
                      className="w-8 h-8"
                      style={{ 
                        backgroundColor: roleInfo.bg,
                        color: roleInfo.color,
                        fontSize: '12px',
                        fontWeight: 'bold'
                      }}
                    >
                      {member.role_name?.charAt(0) || 'U'}
                    </Avatar>
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <div className="flex items-center gap-2">
                        <Typography variant="body2" className="font-semibold text-gray-900" style={{ fontSize: '13px' }}>
                          {member.role_name || 'Unknown Role'}
                        </Typography>
                        <Chip
                          label={member.status}
                          size="small"
                          style={{
                            backgroundColor: member.status === 'valid' ? '#d1fae5' : '#fee2e2',
                            color: member.status === 'valid' ? '#065f46' : '#991b1b',
                            fontSize: '9px',
                            height: '18px',
                            fontWeight: 'bold'
                          }}
                        />
                      </div>
                    }
                    secondary={
                      <div className="space-y-1 mt-1">
                        <Typography variant="body2" className="text-gray-600 flex items-center gap-1" style={{ fontSize: '11px' }}>
                          <Email style={{ fontSize: '12px' }} />
                          {member.email}
                        </Typography>
                        <Typography variant="body2" className="text-gray-500" style={{ fontSize: '10px' }}>
                          User ID: {member.user_id}
                        </Typography>
                      </div>
                    }
                  />
                </MenuItem>
              );
            })}
          </>
        ) : (
          <Box className="p-4 text-center">
            <Typography variant="body2" className="text-gray-500" style={{ fontSize: '12px' }}>
              No support team members available
            </Typography>
          </Box>
        )}
      </Menu>

      {/* Chat Dialog */}
      <Dialog
        open={openChatDialog && !!selectedMeeting}
        onClose={handleCloseChatDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: '16px',
            height: '80vh',
            maxHeight: '800px',
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        <DialogTitle 
          className="pb-2"
          style={{ 
            fontSize: '18px', 
            fontWeight: 'bold',
            color: '#1f2937',
            borderBottom: '2px solid #e5e7eb',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
            backgroundColor: '#10B981',
            color: 'white'
          }}
        >
          <div className="flex items-center gap-2">
            <ChatIcon style={{ fontSize: '22px' }} />
            <div>
              <Typography variant="h6" style={{ fontSize: '16px', fontWeight: 'bold', color: 'white' }}>
                Chat - Meeting #{selectedMeeting?.meeting_id}
              </Typography>
              <Typography variant="body2" style={{ fontSize: '11px', color: 'rgba(255,255,255,0.9)' }}>
                {selectedMeeting?.topic}
              </Typography>
            </div>
          </div>
          <IconButton 
            size="small" 
            onClick={handleCloseChatDialog}
            className="text-white hover:bg-white hover:bg-opacity-20"
          >
            <Close style={{ fontSize: '20px' }} />
          </IconButton>
        </DialogTitle>

        <DialogContent 
          className="flex-1 overflow-y-auto"
          style={{ 
            padding: '16px',
            backgroundColor: '#f9fafb',
            flexGrow: 1,
            overflowY: 'auto'
          }}
        >
          {loadingChat ? (
            <Box className="flex justify-center items-center h-full">
              <CircularProgress size={40} />
            </Box>
          ) : chatError ? (
            <Alert severity="error" className="mb-4">
              <Typography variant="body2" style={{ fontSize: '13px' }}>
                {chatError}
              </Typography>
            </Alert>
          ) : chatMessages.length === 0 ? (
            <Box className="flex flex-col items-center justify-center h-full">
              <ChatIcon style={{ fontSize: '48px', color: '#d1d5db' }} />
              <Typography variant="body1" className="text-gray-500 mt-4" style={{ fontSize: '14px' }}>
                No messages yet. Start the conversation!
              </Typography>
            </Box>
          ) : (
            <div className="space-y-3">
              {chatMessages.map((message, index) => {
                const senderInfo = getMessageSenderInfo(message);
                const isFirstInGroup = index === 0 || 
                  getMessageSenderInfo(chatMessages[index - 1]).isSpoc !== senderInfo.isSpoc;
                
                return (
                  <div
                    key={message.id || index}
                    className={`flex ${senderInfo.isSpoc ? 'justify-start' : 'justify-end'}`}
                  >
                    <div className={`flex gap-2 max-w-[75%] ${senderInfo.isSpoc ? 'flex-row' : 'flex-row-reverse'}`}>
                      {/* Avatar - only show for first message in group */}
                      {isFirstInGroup && (
                        <Avatar
                          className="w-8 h-8"
                          style={{
                            background: senderInfo.avatarBg,
                            fontSize: '12px',
                            fontWeight: 'bold',
                            flexShrink: 0
                          }}
                        >
                          {senderInfo.label.charAt(0)}
                        </Avatar>
                      )}
                      
                      {/* Spacer for grouped messages */}
                      {!isFirstInGroup && (
                        <div className="w-8 flex-shrink-0" />
                      )}
                      
                      {/* Message bubble */}
                      <div className="flex-1">
                        {/* Sender label - only for first in group */}
                        {isFirstInGroup && (
                          <div className={`flex items-center gap-2 mb-1 ${senderInfo.isSpoc ? 'justify-start' : 'justify-end'}`}>
                            <Chip
                              label={senderInfo.label}
                              size="small"
                              style={{
                                backgroundColor: senderInfo.bgColor,
                                color: senderInfo.color,
                                fontSize: '10px',
                                height: '20px',
                                fontWeight: 'bold'
                              }}
                            />
                          </div>
                        )}
                        
                        {/* Message content */}
                        <Card
                          className={`p-3 shadow-md ${
                            senderInfo.isSpoc 
                              ? 'bg-white border-l-4' 
                              : 'bg-gradient-to-r from-green-500 to-emerald-600 text-white'
                          }`}
                          style={{
                            borderRadius: '12px',
                            borderLeftColor: senderInfo.isSpoc ? senderInfo.color : 'transparent'
                          }}
                        >
                          <div className="flex items-start justify-between">
                            <Typography 
                              variant="body2" 
                              style={{ 
                                fontSize: '13px',
                                color: senderInfo.isSpoc ? '#1f2937' : 'white',
                                wordWrap: 'break-word',
                                flex: 1
                              }}
                            >
                              {message.message}
                            </Typography>
                            {message.sending && (
                              <div className="ml-2 flex-shrink-0">
                                <CircularProgress size={12} style={{ color: senderInfo.isSpoc ? '#9ca3af' : 'rgba(255,255,255,0.8)' }} />
                              </div>
                            )}
                          </div>
                          
                          <Typography 
                            variant="caption" 
                            style={{ 
                              fontSize: '10px',
                              color: senderInfo.isSpoc ? '#9ca3af' : 'rgba(255,255,255,0.8)',
                              marginTop: '8px',
                              display: 'block'
                            }}
                          >
                            {formatChatTimestamp(message.created_at)}
                          </Typography>
                        </Card>
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={chatMessagesEndRef} />
            </div>
          )}
        </DialogContent>

        <DialogActions 
          className="border-t border-gray-200 p-4"
          style={{ 
            flexShrink: 0,
            backgroundColor: 'white'
          }}
        >
          <Box className="flex flex-col gap-2 w-full">
            <Box className="flex gap-2">
              <TextField
                fullWidth
                multiline
                maxRows={3}
                variant="outlined"
                placeholder="Type your message..."
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    sendChatMessage();
                  }
                }}
                disabled={sendingMessage || connectionStatus !== 'connected'}
                size="small"
                InputProps={{
                  style: { fontSize: '13px', borderRadius: '12px' }
                }}
              />
              <Button
                variant="contained"
                onClick={sendChatMessage}
                disabled={!newMessage.trim() || sendingMessage || connectionStatus !== 'connected'}
                style={{
                  borderRadius: '12px',
                  minWidth: '100px',
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  textTransform: 'none',
                  fontSize: '13px',
                  fontWeight: 'bold',
                  boxShadow: '0 4px 6px rgba(16, 185, 129, 0.3)'
                }}
              >
                {sendingMessage ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <>
                    <Send style={{ fontSize: '16px', marginRight: '4px' }} />
                    Send
                  </>
                )}
              </Button>
            </Box>
            <Typography variant="caption" style={{ fontSize: '10px', color: '#6b7280' }}>
              Press Enter to send, Shift+Enter for new line
            </Typography>
          </Box>
        </DialogActions>
      </Dialog>

      {/* View Meeting Dialog */}
      <Dialog
        open={openViewDialog && !!selectedMeeting}
        onClose={handleCloseViewDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: '16px',
            padding: '8px',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle 
          className="pb-2"
          style={{ 
            fontSize: '20px', 
            fontWeight: 'bold',
            color: '#1f2937',
            borderBottom: '1px solid #f3f4f6',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <div className="flex items-center gap-2">
            <Assignment style={{ fontSize: '24px', color: '#3b82f6' }} />
            Meeting Details - #{selectedMeeting?.meeting_id}
          </div>
          <IconButton 
            size="small" 
            onClick={handleCloseViewDialog}
            className="text-gray-400 hover:text-gray-600"
          >
            <Close style={{ fontSize: '20px' }} />
          </IconButton>
        </DialogTitle>

        <DialogContent className="space-y-4">
          {selectedMeeting && (
            <div className="space-y-4">
              {/* Educator Information */}
              <Card className="border border-blue-200 bg-blue-50 rounded-lg p-4">
                <Typography variant="h6" className="font-bold text-blue-800 mb-3 flex items-center gap-2" style={{ fontSize: '16px' }}>
                  <Person style={{ fontSize: '18px', color: '#3b82f6' }} />
                  Educator Information
                </Typography>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3">
                    <Avatar className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-bold">
                      {getInitials(selectedMeeting.educator_name)}
                    </Avatar>
                    <div>
                      <Typography variant="body1" className="font-bold text-gray-900" style={{ fontSize: '14px' }}>
                        {selectedMeeting.educator_name}
                      </Typography>
                      <div className="flex items-center gap-1 text-gray-600">
                        <Email style={{ fontSize: '14px' }} />
                        <Typography variant="body2" style={{ fontSize: '12px' }}>
                          {selectedMeeting.educator_email}
                        </Typography>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-gray-600">
                      <Phone style={{ fontSize: '14px' }} />
                      <Typography variant="body2" style={{ fontSize: '12px' }}>
                        {selectedMeeting.mobile}
                      </Typography>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <LocationOn style={{ fontSize: '14px' }} />
                      <Typography variant="body2" style={{ fontSize: '12px' }}>
                        {selectedMeeting.state_name}
                      </Typography>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Institute Information */}
              <Card className="border border-gray-200 rounded-lg p-4">
                <Typography variant="h6" className="font-bold text-gray-900 mb-2 flex items-center gap-2" style={{ fontSize: '16px' }}>
                  <Business style={{ fontSize: '18px', color: '#3b82f6' }} />
                  Institute Information
                </Typography>
                <Typography variant="body1" className="text-gray-700 font-semibold" style={{ fontSize: '14px' }}>
                  {selectedMeeting.institute_name}
                </Typography>
                <Typography variant="body2" className="text-gray-500" style={{ fontSize: '12px' }}>
                  State: {selectedMeeting.state_name}
                </Typography>
              </Card>

              {/* Meeting Details */}
              <Card className="border border-gray-200 rounded-lg p-4">
                <Typography variant="h6" className="font-bold text-gray-900 mb-3 flex items-center gap-2" style={{ fontSize: '16px' }}>
                  <Subject style={{ fontSize: '18px', color: '#3b82f6' }} />
                  Meeting Information
                </Typography>
                <div className="space-y-3">
                  <div>
                    <Typography variant="body2" className="font-semibold text-gray-700 mb-1" style={{ fontSize: '13px' }}>
                      Topic:
                    </Typography>
                    <Typography variant="body1" className="text-gray-900" style={{ fontSize: '14px' }}>
                      {selectedMeeting.topic}
                    </Typography>
                  </div>
                  <div>
                    <Typography variant="body2" className="font-semibold text-gray-700 mb-1" style={{ fontSize: '13px' }}>
                      Description:
                    </Typography>
                    <Typography variant="body1" className="text-gray-700" style={{ fontSize: '14px' }}>
                      {selectedMeeting.description}
                    </Typography>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <div>
                      <Typography variant="body2" className="font-semibold text-gray-700 mb-1" style={{ fontSize: '13px' }}>
                        Created At:
                      </Typography>
                      <Typography variant="body2" className="text-gray-600" style={{ fontSize: '12px' }}>
                        {formatDate(selectedMeeting.created_at)}
                      </Typography>
                    </div>
                    <div>
                      <Typography variant="body2" className="font-semibold text-gray-700 mb-1" style={{ fontSize: '13px' }}>
                        Updated At:
                      </Typography>
                      <Typography variant="body2" className="text-gray-600" style={{ fontSize: '12px' }}>
                        {formatDate(selectedMeeting.updated_at)}
                      </Typography>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Meeting Link */}
              {selectedMeeting.metting_link && (
                <Card className="border border-blue-200 bg-blue-50 rounded-lg p-4">
                  <Typography variant="h6" className="font-bold text-blue-800 mb-2 flex items-center gap-2" style={{ fontSize: '16px' }}>
                    <VideoCall style={{ fontSize: '18px', color: '#3b82f6' }} />
                    Meeting Link
                  </Typography>
                  <div className="flex items-center justify-between">
                    <Typography variant="body2" className="text-blue-700 font-mono" style={{ fontSize: '13px' }}>
                      {selectedMeeting.metting_link}
                    </Typography>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => handleJoinMeeting(selectedMeeting.metting_link)}
                      style={{
                        backgroundColor: '#3b82f6',
                        textTransform: 'none',
                        fontSize: '12px'
                      }}
                    >
                      Join Meeting
                    </Button>
                  </div>
                </Card>
              )}

              {/* Attachments */}
              {selectedMeeting.attachments && selectedMeeting.attachments.length > 0 && (
                <Card className="border border-gray-200 rounded-lg p-4">
                  <Typography variant="h6" className="font-bold text-gray-900 mb-3 flex items-center gap-2" style={{ fontSize: '16px' }}>
                    <AttachFile style={{ fontSize: '18px', color: '#3b82f6' }} />
                    Attachments ({selectedMeeting.attachments.length})
                  </Typography>
                  
                  <List className="space-y-2">
                    {selectedMeeting.attachments.map((attachment, index) => (
                      <React.Fragment key={index}>
                        <ListItem className="border border-gray-100 rounded-lg px-3 py-2 hover:bg-gray-50">
                          <ListItemAvatar>
                            <Avatar className="w-8 h-8 bg-blue-50 text-blue-600" style={{ fontSize: '12px' }}>
                              {getFileIcon(attachment.attachment_name)}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Typography variant="body2" className="font-semibold text-gray-900" style={{ fontSize: '13px' }}>
                                {getFileName(attachment.attachment_name)}
                              </Typography>
                            }
                            secondary={
                              <Typography variant="body2" className="text-gray-500" style={{ fontSize: '11px' }}>
                                {getFileSize(attachment.attachment_name)} • {attachment.attachment_name?.split('.').pop()?.toUpperCase() || 'FILE'}
                              </Typography>
                            }
                          />
                          <Tooltip title="Download File" arrow>
                            <IconButton
                              size="small"
                              onClick={() => handleDownloadAttachment(attachment)}
                              className="ml-2 text-blue-600 hover:bg-blue-50"
                            >
                              <GetApp style={{ fontSize: '16px' }} />
                            </IconButton>
                          </Tooltip>
                        </ListItem>
                        {index < selectedMeeting.attachments.length - 1 && (
                          <Divider variant="middle" />
                        )}
                      </React.Fragment>
                    ))}
                  </List>
                </Card>
              )}
            </div>
          )}
        </DialogContent>

        <DialogActions className="px-6 pb-4 pt-2">
          <Button 
            onClick={handleCloseViewDialog}
            variant="outlined"
            size="small"
            style={{
              borderRadius: '8px',
              textTransform: 'none',
              fontSize: '13px',
              padding: '6px 16px',
              borderColor: '#d1d5db',
              color: '#6b7280'
            }}
          >
            Close
          </Button>
          {selectedMeeting?.metting_link && (
            <Button 
              onClick={() => handleJoinMeeting(selectedMeeting.metting_link)}
              variant="contained"
              size="small"
              style={{
                borderRadius: '8px',
                textTransform: 'none',
                fontSize: '13px',
                padding: '6px 16px',
                backgroundColor: '#3b82f6',
                boxShadow: '0 2px 4px rgba(59, 130, 246, 0.2)'
              }}
            >
              Join Meeting
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Solve Meeting Dialog */}
      <Dialog
        open={openSolveDialog && !!selectedMeeting}
        onClose={handleCloseSolveDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: '16px',
            padding: '8px',
            boxShadow: '0 20px 25px rgba(0,0,0,0.15)'
          }
        }}
      >
        <DialogTitle 
          className="pb-2"
          style={{ 
            fontSize: '18px', 
            fontWeight: 'bold',
            color: '#1f2937',
            borderBottom: '1px solid #f3f4f6',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <div className="flex items-center gap-2">
            <Task style={{ fontSize: '20px', color: '#10b981' }} />
            Solve Meeting Issue
          </div>
          <IconButton 
            size="small" 
            onClick={handleCloseSolveDialog}
            className="text-gray-400 hover:text-gray-600"
          >
            <Close style={{ fontSize: '18px' }} />
          </IconButton>
        </DialogTitle>

        <DialogContent className="space-y-4">
          {selectedMeeting && (
            <div className="space-y-4">
              {/* Meeting Summary */}
              <Card className="border border-blue-200 bg-blue-50 rounded-lg p-4">
                <Typography variant="body2" className="font-semibold text-blue-800 mb-2" style={{ fontSize: '13px' }}>
                  Meeting Summary
                </Typography>
                <Typography variant="body1" className="text-blue-900 font-bold mb-1" style={{ fontSize: '14px' }}>
                  {selectedMeeting.topic}
                </Typography>
                <Typography variant="body2" className="text-blue-700" style={{ fontSize: '12px' }}>
                  Educator: {selectedMeeting.educator_name} • Meeting ID: #{selectedMeeting.meeting_id}
                </Typography>
              </Card>

              {/* Success/Error Messages */}
              {solveSuccess && (
                <Alert severity="success" className="rounded-lg">
                  <Typography variant="body2" style={{ fontSize: '13px' }}>
                    {solveSuccess}
                  </Typography>
                </Alert>
              )}
              
              {solveError && (
                <Alert severity="error" className="rounded-lg">
                  <Typography variant="body2" style={{ fontSize: '13px' }}>
                    {solveError}
                  </Typography>
                </Alert>
              )}

              {/* Closing Remark Input */}
              <div>
                <Typography variant="body2" className="font-semibold text-gray-700 mb-2" style={{ fontSize: '14px' }}>
                  Closing Remark *
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  variant="outlined"
                  placeholder="Please provide a detailed explanation of how the meeting issue was resolved..."
                  value={closingRemark}
                  onChange={(e) => setClosingRemark(e.target.value)}
                  disabled={solvingLoading}
                  error={!!solveError && !closingRemark.trim()}
                  helperText={!!solveError && !closingRemark.trim() ? "Closing remark is required" : ""}
                  InputProps={{
                    style: { fontSize: '13px' }
                  }}
                />
              </div>
            </div>
          )}
        </DialogContent>

        <DialogActions className="px-6 pb-4 pt-2">
          <Button 
            onClick={handleCloseSolveDialog}
            variant="outlined"
            size="small"
            disabled={solvingLoading}
            style={{
              borderRadius: '8px',
              textTransform: 'none',
              fontSize: '13px',
              padding: '6px 16px',
              borderColor: '#d1d5db',
              color: '#6b7280'
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSolveMeeting}
            variant="contained"
            size="small"
            disabled={solvingLoading || !closingRemark.trim()}
            style={{
              borderRadius: '8px',
              textTransform: 'none',
              fontSize: '13px',
              padding: '6px 16px',
              backgroundColor: '#10b981',
              boxShadow: '0 2px 4px rgba(16, 185, 129, 0.2)',
              minWidth: '100px'
            }}
          >
            {solvingLoading ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              'Mark as Solved'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default StaffMeetingsTable;
